
import { useState, useEffect, useCallback, useRef } from "react";

interface UseWeightSelectorLogicProps {
  value: number;
  onChange: (value: string) => void;
  disabled?: boolean;
  exerciseId?: string;
  setIndex?: number;
  isPersonalPlan?: boolean;
}

export const useWeightSelectorLogic = ({
  value,
  onChange,
  disabled = false,
  exerciseId = "",
  setIndex = 0,
  isPersonalPlan = false
}: UseWeightSelectorLogicProps) => {
  const [localValue, setLocalValue] = useState<string>(value?.toString() || "");
  const [isFocused, setIsFocused] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<number | null>(null);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const syncTimeoutRef = useRef<NodeJS.Timeout>();
  const isUpdatingRef = useRef(false);
  const lastExternalValueRef = useRef(value);
  const localBackupRef = useRef<number>(value);

  // Sync externo ultra-optimizado
  useEffect(() => {
    if (!isFocused && !isUpdatingRef.current && lastExternalValueRef.current !== value) {
      console.log(`⚡ Ultra-fast external sync for weight selector:`, {
        from: lastExternalValueRef.current,
        to: value,
        exerciseId,
        setIndex
      });
      
      setLocalValue(value?.toString() || "");
      lastExternalValueRef.current = value;
      localBackupRef.current = value;
      setHasUnsavedChanges(false);
      setLastSyncTime(null);
    }
  }, [value, isFocused, exerciseId, setIndex]);

  // Simulación de sincronización ultra-rápida para planes personales
  const performUltraFastSync = useCallback(async (newValue: number) => {
    if (!isPersonalPlan) {
      onChange(newValue.toString());
      return;
    }

    const startTime = Date.now();
    setIsSyncing(true);
    setHasUnsavedChanges(true);
    isUpdatingRef.current = true;

    console.log(`⚡ Ultra-fast sync initiated for exercise ${exerciseId}, set ${setIndex}:`, newValue);

    // Backup local inmediato
    localBackupRef.current = newValue;

    // Cancelar sincronización anterior
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }

    // Llamar onChange inmediatamente
    onChange(newValue.toString());

    // Confirmar sincronización ultra-rápida (150ms para planes personales)
    syncTimeoutRef.current = setTimeout(() => {
      const syncTime = Date.now() - startTime;
      
      setIsSyncing(false);
      setHasUnsavedChanges(false);
      setLastSyncTime(syncTime);
      lastExternalValueRef.current = newValue;
      
      // Permitir actualizaciones externas después de un breve delay
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 50);
      
      console.log(`✅ Ultra-fast weight sync completed in ${syncTime}ms for exercise ${exerciseId}, set ${setIndex}`);
    }, isPersonalPlan ? 150 : 300);
  }, [isPersonalPlan, exerciseId, setIndex, onChange]);

  const updateValue = useCallback((newValue: number | null) => {
    const numValue = newValue !== null ? newValue : 0;
    const stringValue = numValue.toString();
    
    console.log(`⚡ Ultra-fast weight update:`, {
      from: localValue,
      to: stringValue,
      numericValue: numValue,
      isPersonalPlan,
      disabled
    });

    if (disabled) return;
    
    setLocalValue(stringValue);
    performUltraFastSync(numValue);
  }, [localValue, performUltraFastSync, isPersonalPlan, disabled]);

  const increment = useCallback(() => {
    if (!isFocused && !disabled) {
      const currentNum = parseFloat(localValue) || 0;
      const newValue = Math.round((currentNum + 0.5) * 2) / 2;
      updateValue(newValue);
    }
  }, [localValue, updateValue, isFocused, disabled]);

  const decrement = useCallback(() => {
    if (!isFocused && !disabled) {
      const currentNum = parseFloat(localValue) || 0;
      const newValue = Math.max(0, Math.round((currentNum - 0.5) * 2) / 2);
      updateValue(newValue);
    }
  }, [localValue, updateValue, isFocused, disabled]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    
    const inputValue = e.target.value;
    
    // Allow empty field
    if (inputValue === "") {
      setLocalValue("");
      return;
    }
    
    const numValue = parseFloat(inputValue);
    
    if (!isNaN(numValue) && numValue >= 0) {
      setLocalValue(inputValue);
    }
  }, [disabled]);

  const handleInputBlur = useCallback(() => {
    setIsFocused(false);
    
    if (disabled) return;
    
    // Handle empty field
    if (localValue === "") {
      updateValue(0);
    } else {
      const numValue = parseFloat(localValue);
      if (!isNaN(numValue)) {
        const roundedValue = Math.round(numValue * 2) / 2;
        updateValue(roundedValue);
      } else {
        // Invalid number - reset to backup
        const backupValue = localBackupRef.current || 0;
        setLocalValue(backupValue.toString());
        updateValue(backupValue);
      }
    }
  }, [localValue, updateValue, disabled]);

  const handleInputFocus = useCallback(() => {
    if (!disabled) {
      setIsFocused(true);
    }
  }, [disabled]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (disabled) return;
    
    if (e.key === "ArrowUp") {
      e.preventDefault();
      increment();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      decrement();
    } else if (e.key === "Enter") {
      inputRef.current?.blur();
    }
  }, [increment, decrement, disabled]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, []);

  return {
    localValue,
    isFocused,
    isSyncing,
    hasUnsavedChanges,
    lastSyncTime,
    inputRef,
    handleInputChange,
    handleInputBlur,
    handleInputFocus,
    handleKeyDown,
    increment,
    decrement
  };
};
